import Stack from "@mui/material/Stack";
import type { Overwrite } from "@mui/types";
import { theme } from "@polifonia/theme";
import { fakeT } from "@polifonia/utils/react/fakeT";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";

type ChannelType = Extract<
  InterludeTypes["Channel"]["type"],
  "whatsapp" | "fb" | "ig"
>;

namespace DisconnectModal {
  export interface OwnProps {
    channelType: ChannelType;
    open: boolean;
    isLoading: boolean;
    onClose: () => void;
    onConfirm: () => void;
  }

  export type Props = Overwrite<
    Omit<ModalProps, "children" | "handleClose">,
    OwnProps
  >;
}
const t = fakeT;
const translationMap: Record<
  ChannelType,
  Record<"title" | "desc", ReturnType<typeof t>>
> = {
  fb: {
    title: t("page.channel.fb.disconnect.modal.title"),
    desc: t("page.channel.fb.disconnect.modal.description"),
  },
  ig: {
    title: t("page.channel.ig.disconnect.modal.title"),
    desc: t("page.channel.ig.disconnect.modal.description"),
  },
  whatsapp: {
    title: t("page.channel.whatsapp.disconnect.modal.title"),
    desc: t("page.channel.whatsapp.disconnect.modal.description"),
  },
};

export const DisconnectModal: FC<DisconnectModal.Props> = ({
  open,
  isLoading,
  onClose,
  onConfirm,
  channelType,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={t(translationMap[channelType].title)}
      footer={
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="plain" onClick={onClose} disabled={isLoading}>
            {t("channels.addModal.footer.cancel")}
          </Button>
          <Button variant="danger" onClick={onConfirm} loading={isLoading}>
            {t("common.disconnect")}
          </Button>
        </Stack>
      }
    >
      <Stack gap="16px">
        <Typography variant="body" color={theme.colors.staticFgBody}>
          {t(translationMap[channelType].desc)}
        </Typography>
      </Stack>
    </Modal>
  );
};
